import { all, put, select, takeLatest } from "redux-saga/effects";
import axios from "axios";

import {
  selectAuthToken,
  selectOrganisationId,
} from "../selectors/login.selector";
import * as API from "../utils/api";
import {
  getWipNotifications,
  setWipNotifications,
  getWipNotificationCount,
  setWipNotificationCount,
  triggerMarkWipNotificationAsRead,
  markWipNotificationAsRead,
  triggerMarkAllWipNotificationsAsRead,
  markAllWipNotificationsAsRead,
  loading,
} from "../reducers/wipNotification.reducer";

function* handleGetWipNotificationsWorker(action) {
  try {
    yield put(loading(true));
    const authToken = yield select(selectAuthToken);
    const organisationId = yield select(selectOrganisationId);

    let requestURL = API.WIP.GET_NOTIFICATIONS.replace(
      "<ORGANISATION_ID>",
      organisationId
    );

    // Build query parameters
    const params = {};
    if (action.payload) {
      const { limit, offset, isRead, notificationType } = action.payload;
      if (limit !== undefined) params.limit = limit;
      if (offset !== undefined) params.offset = offset;
      if (isRead !== undefined) params.isRead = isRead;
      if (notificationType) params.notificationType = notificationType;
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    const response = yield axios.get(requestURL, { headers, params });
    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        yield put(
          setWipNotifications({
            notifications: response.data.results.notifications || [],
            pagination: response.data.results.pagination || {},
          })
        );
      }
    }
  } catch (error) {
    console.error("Failed to get WIP notifications:", error);
  } finally {
    yield put(loading(false));
  }
}

function* handleGetWipNotificationCountWorker() {
  try {
    const authToken = yield select(selectAuthToken);
    const organisationId = yield select(selectOrganisationId);
    const requestURL = API.WIP.GET_NOTIFICATION_COUNT.replace(
      "<ORGANISATION_ID>",
      organisationId
    );

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    const response = yield axios.get(requestURL, { headers });
    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        yield put(
          setWipNotificationCount(response.data.results.unreadCount || 0)
        );
      }
    }
  } catch (error) {
    console.error("Failed to get WIP notification count:", error);
  }
}

function* handleMarkWipNotificationAsReadWorker(action) {
  try {
    const authToken = yield select(selectAuthToken);
    const organisationId = yield select(selectOrganisationId);
    const requestURL = API.WIP.MARK_NOTIFICATION_READ.replace(
      "<ORGANISATION_ID>",
      organisationId
    ).replace("<NOTIFICATION_ID>", action.payload.notificationId);

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    const response = yield axios.put(requestURL, {}, { headers });
    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        yield put(
          markWipNotificationAsRead({
            _id: action.payload.notificationId,
            readAt: response.data.results.readAt,
          })
        );
        // Refresh count
        yield put(getWipNotificationCount());
      }
    }
  } catch (error) {
    console.error("Failed to mark WIP notification as read:", error);
  }
}

function* handleMarkAllWipNotificationsAsReadWorker() {
  try {
    const authToken = yield select(selectAuthToken);
    const organisationId = yield select(selectOrganisationId);
    const requestURL = API.WIP.MARK_ALL_NOTIFICATIONS_READ.replace(
      "<ORGANISATION_ID>",
      organisationId
    );

    const headers = {
      "Content-Type": "application/json",
      Authorization: authToken,
    };

    const response = yield axios.put(requestURL, {}, { headers });
    if (response && response.status === 200 && response.data) {
      if (response.data.success) {
        yield put(markAllWipNotificationsAsRead());
        yield put(setWipNotificationCount(0));
      }
    }
  } catch (error) {
    console.error("Failed to mark all WIP notifications as read:", error);
  }
}

function* handleGetWipNotificationsSaga() {
  yield takeLatest(getWipNotifications.type, handleGetWipNotificationsWorker);
}

function* handleGetWipNotificationCountSaga() {
  yield takeLatest(
    getWipNotificationCount.type,
    handleGetWipNotificationCountWorker
  );
}

function* handleMarkWipNotificationAsReadSaga() {
  yield takeLatest(
    triggerMarkWipNotificationAsRead.type,
    handleMarkWipNotificationAsReadWorker
  );
}

function* handleMarkAllWipNotificationsAsReadSaga() {
  yield takeLatest(
    triggerMarkAllWipNotificationsAsRead.type,
    handleMarkAllWipNotificationsAsReadWorker
  );
}

export default function* wipNotificationSaga() {
  yield all([
    handleGetWipNotificationsSaga(),
    handleGetWipNotificationCountSaga(),
    handleMarkWipNotificationAsReadSaga(),
    handleMarkAllWipNotificationsAsReadSaga(),
  ]);
}

